import type { BrandKit } from "#gen/brands/v1/service_pb.js";
import type { Page } from "#gen/pages/v2/page_pb.js";
import { FcColorWhite } from "#lib/code.js";
import { ELEMENT_DEFAULTS, FULL_BORDER_RADIUS } from "#lib/components/flow/editor/appearance/constants.js";
import type { JsonObject, JsonValue } from "#lib/protobuf-compat.js";
import { booleanValue, setValue, stringValue, unsetValue } from "#lib/struct.js";
import { DEFAULT_THEME_COLORS, getPrimaryColor, getSecondaryColor, getTextColor, isLight, lightenDarkenColor, styleString } from "#lib/theme.js";
import type { SizeType } from "flowbite-svelte-v1";

export function getThemes(brandKit: BrandKit | undefined) {
	return [
		{ value: "base", label: "Brand", topColor: getPrimaryColor(brandKit)?.hex, bottomColor: getSecondaryColor(brandKit)?.hex },
		{ value: "custom", label: "Custom", topColor: DEFAULT_THEME_COLORS.default.primaryButtonBgColor, bottomColor: DEFAULT_THEME_COLORS.default.secondaryButtonBgColor },
	];
}

function getDefaultTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}) {
	const backgroundColor = stringValue(config, "backgroundColor") ?? stringValue(page.themeOverrides, "backgroundColor");
	return getTextColor(backgroundColor ?? FcColorWhite);
}

function getHeaderTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "headerTextColor") ?? stringValue(page.themeOverrides, "headerTextColor") ?? getPrimaryTextColor(brandKit, page, config);
}

function getHeaderTextSize(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "headerTextSize") || ELEMENT_DEFAULTS["h1"].textSize;
}

function getHeaderTextAlignment(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "headerTextAlignment") ?? ELEMENT_DEFAULTS["h1"].textAlignment;
}

function getHeaderFontWeight(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "headerFontWeight") ?? ELEMENT_DEFAULTS["h1"].fontWeight;
}

function getPrimaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "primaryTextColor") ?? stringValue(page.themeOverrides, "paragraphTextColor") ?? getDefaultTextColor(brandKit, page, config);
}

function getSecondaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "secondaryTextColor") ?? getDefaultTextColor(brandKit, page, config);
}

function getPrimaryButtonBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "primaryButtonBackgroundColor") ?? stringValue(page.themeOverrides, "primaryButtonBackgroundColor") ?? getPrimaryColor(brandKit)?.hex ?? DEFAULT_THEME_COLORS.default.primaryButtonBgColor;
}

function getPrimaryButtonTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	let color = stringValue(config, "primaryButtonTextColor") ?? stringValue(page.themeOverrides, "primaryButtonTextColor");
	if (!color) {
		const backgroundColor = getPrimaryButtonBackgroundColor(brandKit, page, config);
		color = getTextColor(backgroundColor);
	}
	if (!color) {
		color = DEFAULT_THEME_COLORS.default.primaryButtonTextColor;
	}
	return color;
}

function getSecondaryButtonBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "secondaryButtonBackgroundColor") ?? stringValue(page.themeOverrides, "secondaryButtonBackgroundColor") ?? getSecondaryColor(brandKit)?.hex ?? DEFAULT_THEME_COLORS.default.secondaryButtonBgColor;
}

function getSecondaryButtonTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	let color = stringValue(config, "secondaryButtonTextColor") ?? stringValue(page.themeOverrides, "secondaryButtonTextColor");
	if (!color) {
		const backgroundColor = getSecondaryButtonBackgroundColor(brandKit, page, config);
		color = getTextColor(backgroundColor);
	}
	if (!color) {
		color = DEFAULT_THEME_COLORS.default.secondaryButtonTextColor;
	}
	return color;
}

function getBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "backgroundColor") ?? stringValue(page.themeOverrides, "backgroundColor") ?? "#F9FAFB";
}

function getBorderColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "borderColor") ?? "#E5E7EB";
}

function getNavigationColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "navigationColor") ?? stringValue(page.themeOverrides, "primaryButtonBackgroundColor") ?? getPrimaryColor(brandKit)?.hex ?? DEFAULT_THEME_COLORS.default.primaryButtonBgColor;
}

function getCardBackgroundColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "cardBackgroundColor") ?? "#FFFFFF";
}

function getCardBorderColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "cardBorderColor") ?? getBorderColor(brandKit, config);
}

function getCardPrimaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "cardPrimaryTextColor") ?? getTextColor(getCardBackgroundColor(brandKit, config) ?? getPrimaryTextColor(brandKit, page, config));
}

function getCardSecondaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "cardSecondaryTextColor") ?? getTextColor(getCardBackgroundColor(brandKit, config) ?? getSecondaryTextColor(brandKit, page, config));
}

function getResultsBackgroundColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "resultsBackgroundColor") ?? "#FFFFFF";
}

function getResultsBorderColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "resultsBorderColor") ?? getBorderColor(brandKit, config);
}

function getResultsPrimaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "resultsPrimaryTextColor") ?? getTextColor(getResultsBackgroundColor(brandKit, config) ?? getPrimaryTextColor(brandKit, page, config));
}

function getResultsSecondaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "resultsSecondaryTextColor") ?? getTextColor(getResultsBackgroundColor(brandKit, config) ?? getSecondaryTextColor(brandKit, page, config));
}

function getResultsPercentageDisabledColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "resultsPercentageDisabledColor") ?? "#D9D9D9";
}

function getResultsPercentageEnabledColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "resultsPercentageEnabledColor") ?? "#686868";
}

function getUserVotedBackgroundColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "userVotedBackgroundColor") ?? "#FFFFFF";
}

function getUserVotedBorderColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "userVotedBorderColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getUserVotedPrimaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "userVotedPrimaryTextColor") ?? getTextColor(getUserVotedBackgroundColor(brandKit, config) ?? getPrimaryTextColor(brandKit, page, config));
}

function getUserVotedSecondaryTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "userVotedSecondaryTextColor") ?? getTextColor(getUserVotedBackgroundColor(brandKit, config) ?? getSecondaryTextColor(brandKit, page, config));
}

function getUserVotedPercentageDisabledColor(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "userVotedPercentageDisabledColor") ?? "#D9D9D9";
}

function getUserVotedPercentageEnabledColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "userVotedPercentageEnabledColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getYourVoteTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "yourVoteTextColor") ?? getPrimaryButtonTextColor(brandKit, page, config);
}

function getYourVoteBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "yourVoteBackgroundColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getYourVoteTextAlignment(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "yourVoteTextAlignment") ?? "center";
}

function getYourVoteFontWeight(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "yourVoteFontWeight") ?? "normal";
}

function getCountdownHeaderTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownHeaderTextColor") ?? getDefaultTextColor(brandKit, page, config);
}

function getCountdownHeaderTextSize(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownHeaderTextSize") ?? "sm";
}

function getCountdownHeaderTextAlignment(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownHeaderTextAlignment") ?? "center";
}

function getCountdownHeaderFontWeight(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownHeaderFontWeight") ?? "normal";
}

function getCountdownDateTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownDateTextColor") ?? getDefaultTextColor(brandKit, page, config);
}

function getCountdownDateTextSize(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownDateTextSize") ?? "sm";
}

function getCountdownDateTextAlignment(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownDateTextAlignment") ?? "center";
}

function getCountdownDateFontWeight(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownDateFontWeight") ?? "normal";
}

function getCountdownBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownBackgroundColor") ?? getSecondaryButtonBackgroundColor(brandKit, page, config);
}

function getCountdownTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "countdownTextColor") ?? getTextColor(getCountdownBackgroundColor(brandKit, page, config)) ?? getSecondaryTextColor(brandKit, page, config);
}

function getHelperTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "helperTextColor") ?? getPrimaryTextColor(brandKit, page, config);
}

function getHelperTextSize(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "helperTextSize") ?? "sm";
}

function getHelperTextAlignment(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "helperTextAlignment") ?? "center";
}

function getHelperFontWeight(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "helperFontWeight") ?? "normal";
}

function getResultsHelperTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "resultsHelperTextColor") ?? getPrimaryTextColor(brandKit, page, config);
}

function getResultsHelperTextSize(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "resultsHelperTextSize") ?? "sm";
}

function getResultsHelperTextAlignment(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "resultsHelperTextAlignment") ?? "center";
}

function getResultsHelperFontWeight(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "resultsHelperFontWeight") ?? "normal";
}

function getHoverColor(color: string): string {
	return lightenDarkenColor(color, isLight(color) ? -20 : 20);
}

export function getDefaults(brandKit: BrandKit | undefined, page: Page): Record<string, JsonValue> {
	return getTheme(brandKit, page);
}

function matchesFallback(value: JsonValue, fallback: JsonValue | undefined): boolean {
	// Colors reach us from the brand kit in whatever case they were stored, so hex compares loosely.
	if (typeof value === "string" && typeof fallback === "string") {
		return value.toLowerCase() === fallback.toLowerCase();
	}
	return value === fallback;
}

export function setThemeValue(brandKit: BrandKit | undefined, page: Page, config: JsonObject, key: string, value: JsonValue | undefined) {
	if (value === undefined || value === null) {
		unsetValue(config, key);
		return;
	}
	const withoutKey = { ...config };
	delete withoutKey[key];
	if (matchesFallback(value, getTheme(brandKit, page, withoutKey)[key])) {
		unsetValue(config, key);
	} else {
		setValue(config, key, value);
	}
}

export function getSkipButtonSize(brandKit: BrandKit | undefined, config: JsonObject = {}): SizeType {
	return (stringValue(config, "skipButtonSize") as SizeType) ?? "md";
}

function getSkipButtonBorderRadius(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "skipButtonBorderRadius") ?? FULL_BORDER_RADIUS;
}

function getSkipButtonBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "skipButtonBackgroundColor") ?? getSecondaryButtonBackgroundColor(brandKit, page, config);
}

function getSkipButtonBorderColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "skipButtonBorderColor") ?? getSecondaryButtonBackgroundColor(brandKit, page, config);
}

function getSkipButtonTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "skipButtonTextColor") ?? getSecondaryButtonTextColor(brandKit, page, config);
}

export function getSubmitButtonSize(brandKit: BrandKit | undefined, config: JsonObject = {}): SizeType {
	return (stringValue(config, "submitButtonSize") as SizeType) ?? "md";
}

function getSubmitButtonBorderRadius(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "submitButtonBorderRadius") ?? FULL_BORDER_RADIUS;
}

function getSubmitButtonBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "submitButtonBackgroundColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getSubmitButtonBorderColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "submitButtonBorderColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getSubmitButtonTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "submitButtonTextColor") ?? getPrimaryButtonTextColor(brandKit, page, config);
}

export function getCompleteButtonSize(brandKit: BrandKit | undefined, config: JsonObject = {}): SizeType {
	return (stringValue(config, "completeButtonSize") as SizeType) ?? "md";
}

function getCompleteButtonBorderRadius(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "completeButtonBorderRadius") ?? FULL_BORDER_RADIUS;
}

function getCompleteButtonBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "completeButtonBackgroundColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getCompleteButtonBorderColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "completeButtonBorderColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getCompleteButtonTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "completeButtonTextColor") ?? getPrimaryButtonTextColor(brandKit, page, config);
}

export function getRestartButtonSize(brandKit: BrandKit | undefined, config: JsonObject = {}): SizeType {
	return (stringValue(config, "restartButtonSize") as SizeType) ?? "md";
}

function getRestartButtonBorderRadius(brandKit: BrandKit | undefined, config: JsonObject = {}): string {
	return stringValue(config, "restartButtonBorderRadius") ?? FULL_BORDER_RADIUS;
}

function getRestartButtonBackgroundColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "restartButtonBackgroundColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getRestartButtonBorderColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "restartButtonBorderColor") ?? getPrimaryButtonBackgroundColor(brandKit, page, config);
}

function getRestartButtonTextColor(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): string {
	return stringValue(config, "restartButtonTextColor") ?? getPrimaryButtonTextColor(brandKit, page, config);
}

export function getShowPollTitlesInResults(config: JsonObject = {}): boolean {
	return booleanValue(config, "showPollTitlesInResults") ?? true;
}

export function getShowPollBorderInResults(config: JsonObject = {}): boolean {
	return booleanValue(config, "showPollBorderInResults") ?? false;
}

export function getShowPollBackgroundColorInResults(config: JsonObject = {}): boolean {
	return booleanValue(config, "showPollBackgroundColorInResults") ?? false;
}

export function getTheme(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}): Record<string, JsonValue> {
	return {
		backgroundColor: getBackgroundColor(brandKit, page, config),
		borderColor: getBorderColor(brandKit, config),
		navigationColor: getNavigationColor(brandKit, page, config),
		primaryTextColor: getPrimaryTextColor(brandKit, page, config),
		secondaryTextColor: getSecondaryTextColor(brandKit, page, config),
		primaryButtonBackgroundColor: getPrimaryButtonBackgroundColor(brandKit, page, config),
		primaryButtonTextColor: getPrimaryButtonTextColor(brandKit, page, config),
		secondaryButtonBackgroundColor: getSecondaryButtonBackgroundColor(brandKit, page, config),
		secondaryButtonTextColor: getSecondaryButtonTextColor(brandKit, page, config),
		headerTextColor: getHeaderTextColor(brandKit, page, config),
		headerTextSize: getHeaderTextSize(brandKit, page, config),
		headerTextAlignment: getHeaderTextAlignment(brandKit, page, config),
		headerFontWeight: getHeaderFontWeight(brandKit, page, config),
		cardBackgroundColor: getCardBackgroundColor(brandKit, config),
		cardBorderColor: getCardBorderColor(brandKit, config),
		cardPrimaryTextColor: getCardPrimaryTextColor(brandKit, page, config),
		cardSecondaryTextColor: getCardSecondaryTextColor(brandKit, page, config),
		resultsBackgroundColor: getResultsBackgroundColor(brandKit, config),
		resultsBorderColor: getResultsBorderColor(brandKit, config),
		resultsPrimaryTextColor: getResultsPrimaryTextColor(brandKit, page, config),
		resultsSecondaryTextColor: getResultsSecondaryTextColor(brandKit, page, config),
		resultsPercentageDisabledColor: getResultsPercentageDisabledColor(brandKit, config),
		resultsPercentageEnabledColor: getResultsPercentageEnabledColor(brandKit, config),
		userVotedBackgroundColor: getUserVotedBackgroundColor(brandKit, config),
		userVotedBorderColor: getUserVotedBorderColor(brandKit, page, config),
		userVotedPrimaryTextColor: getUserVotedPrimaryTextColor(brandKit, page, config),
		userVotedSecondaryTextColor: getUserVotedSecondaryTextColor(brandKit, page, config),
		userVotedPercentageDisabledColor: getUserVotedPercentageDisabledColor(brandKit, config),
		userVotedPercentageEnabledColor: getUserVotedPercentageEnabledColor(brandKit, page, config),
		yourVoteTextColor: getYourVoteTextColor(brandKit, page, config),
		yourVoteBackgroundColor: getYourVoteBackgroundColor(brandKit, page, config),
		yourVoteTextAlignment: getYourVoteTextAlignment(brandKit, page, config),
		yourVoteFontWeight: getYourVoteFontWeight(brandKit, page, config),
		countdownBackgroundColor: getCountdownBackgroundColor(brandKit, page, config),
		countdownTextColor: getCountdownTextColor(brandKit, page, config),
		countdownHeaderTextColor: getCountdownHeaderTextColor(brandKit, page, config),
		countdownHeaderTextSize: getCountdownHeaderTextSize(brandKit, page, config),
		countdownHeaderTextAlignment: getCountdownHeaderTextAlignment(brandKit, page, config),
		countdownHeaderFontWeight: getCountdownHeaderFontWeight(brandKit, page, config),
		countdownDateTextColor: getCountdownDateTextColor(brandKit, page, config),
		countdownDateTextSize: getCountdownDateTextSize(brandKit, page, config),
		countdownDateTextAlignment: getCountdownDateTextAlignment(brandKit, page, config),
		countdownDateFontWeight: getCountdownDateFontWeight(brandKit, page, config),
		helperTextColor: getHelperTextColor(brandKit, page, config),
		helperTextSize: getHelperTextSize(brandKit, page, config),
		helperTextAlignment: getHelperTextAlignment(brandKit, page, config),
		helperFontWeight: getHelperFontWeight(brandKit, page, config),
		resultsHelperTextColor: getResultsHelperTextColor(brandKit, page, config),
		resultsHelperTextSize: getResultsHelperTextSize(brandKit, page, config),
		resultsHelperTextAlignment: getResultsHelperTextAlignment(brandKit, page, config),
		resultsHelperFontWeight: getResultsHelperFontWeight(brandKit, page, config),
		skipButtonSize: getSkipButtonSize(brandKit, config),
		skipButtonBorderRadius: getSkipButtonBorderRadius(brandKit, config),
		skipButtonBackgroundColor: getSkipButtonBackgroundColor(brandKit, page, config),
		skipButtonBorderColor: getSkipButtonBorderColor(brandKit, page, config),
		skipButtonTextColor: getSkipButtonTextColor(brandKit, page, config),
		submitButtonSize: getSubmitButtonSize(brandKit, config),
		submitButtonBorderRadius: getSubmitButtonBorderRadius(brandKit, config),
		submitButtonBackgroundColor: getSubmitButtonBackgroundColor(brandKit, page, config),
		submitButtonBorderColor: getSubmitButtonBorderColor(brandKit, page, config),
		submitButtonTextColor: getSubmitButtonTextColor(brandKit, page, config),
		completeButtonSize: getCompleteButtonSize(brandKit, config),
		completeButtonBorderRadius: getCompleteButtonBorderRadius(brandKit, config),
		completeButtonBackgroundColor: getCompleteButtonBackgroundColor(brandKit, page, config),
		completeButtonBorderColor: getCompleteButtonBorderColor(brandKit, page, config),
		completeButtonTextColor: getCompleteButtonTextColor(brandKit, page, config),
		restartButtonSize: getRestartButtonSize(brandKit, config),
		restartButtonBorderRadius: getRestartButtonBorderRadius(brandKit, config),
		restartButtonBackgroundColor: getRestartButtonBackgroundColor(brandKit, page, config),
		restartButtonBorderColor: getRestartButtonBorderColor(brandKit, page, config),
		restartButtonTextColor: getRestartButtonTextColor(brandKit, page, config),
		showPollTitlesInResults: getShowPollTitlesInResults(config),
		showPollBorderInResults: getShowPollBorderInResults(config),
		showPollBackgroundColorInResults: getShowPollBackgroundColorInResults(config),
	};
}

export function getPollStyleVars(brandKit: BrandKit | undefined, page: Page, config: JsonObject = {}) {
	return styleString({
		"--poll-primary-text-color": getPrimaryTextColor(brandKit, page, config),
		"--poll-secondary-text-color": getSecondaryTextColor(brandKit, page, config),
		"--poll-background-color": getBackgroundColor(brandKit, page, config),
		"--poll-border-color": getBorderColor(brandKit, config),
		"--poll-header-text-color": getHeaderTextColor(brandKit, page, config),
		"--poll-header-text-size": "var(--text-" + getHeaderTextSize(brandKit, page, config) + ")",
		"--poll-header-text-alignment": getHeaderTextAlignment(brandKit, page, config),
		"--poll-header-font-weight": getHeaderFontWeight(brandKit, page, config),
		"--poll-primary-button-background-color": getPrimaryButtonBackgroundColor(brandKit, page, config),
		"--poll-priamry-button-background-hover-color": getHoverColor(getPrimaryButtonBackgroundColor(brandKit, page, config)),
		"--poll-primary-button-text-color": getPrimaryButtonTextColor(brandKit, page, config),
		"--poll-secondary-button-background-color": getSecondaryButtonBackgroundColor(brandKit, page, config),
		"--poll-secondary-button-background-hover-color": getHoverColor(getSecondaryButtonBackgroundColor(brandKit, page, config)),
		"--poll-secondary-button-text-color": getSecondaryButtonTextColor(brandKit, page, config),
		"--poll-navigation-color": getNavigationColor(brandKit, page, config),
		"--poll-card-background-color": getCardBackgroundColor(brandKit, config),
		"--poll-card-border-color": getCardBorderColor(brandKit, config),
		"--poll-card-primary-text-color": getCardPrimaryTextColor(brandKit, page, config),
		"--poll-card-secondary-text-color": getCardSecondaryTextColor(brandKit, page, config),
		"--poll-results-background-color": getResultsBackgroundColor(brandKit, config),
		"--poll-results-border-color": getResultsBorderColor(brandKit, config),
		"--poll-results-primary-text-color": getResultsPrimaryTextColor(brandKit, page, config),
		"--poll-results-secondary-text-color": getResultsSecondaryTextColor(brandKit, page, config),
		"--poll-results-percentage-disabled-color": getResultsPercentageDisabledColor(brandKit, config),
		"--poll-results-percentage-enabled-color": getResultsPercentageEnabledColor(brandKit, config),
		"--poll-user-voted-background-color": getUserVotedBackgroundColor(brandKit, config),
		"--poll-user-voted-border-color": getUserVotedBorderColor(brandKit, page, config),
		"--poll-user-voted-primary-text-color": getUserVotedPrimaryTextColor(brandKit, page, config),
		"--poll-user-voted-secondary-text-color": getUserVotedSecondaryTextColor(brandKit, page, config),
		"--poll-user-voted-percentage-disabled-color": getUserVotedPercentageDisabledColor(brandKit, config),
		"--poll-user-voted-percentage-enabled-color": getUserVotedPercentageEnabledColor(brandKit, page, config),
		"--poll-your-vote-background-color": getYourVoteBackgroundColor(brandKit, page, config),
		"--poll-your-vote-text-color": getYourVoteTextColor(brandKit, page, config),
		"--poll-your-vote-text-alignment": getYourVoteTextAlignment(brandKit, page, config),
		"--poll-your-vote-font-weight": getYourVoteFontWeight(brandKit, page, config),
		"--poll-countdown-background-color": getCountdownBackgroundColor(brandKit, page, config),
		"--poll-countdown-text-color": getCountdownTextColor(brandKit, page, config),
		"--poll-countdown-header-text-color": getCountdownHeaderTextColor(brandKit, page, config),
		"--poll-countdown-header-text-size": "var(--text-" + getCountdownHeaderTextSize(brandKit, page, config) + ")",
		"--poll-countdown-header-text-alignment": getCountdownHeaderTextAlignment(brandKit, page, config),
		"--poll-countdown-header-font-weight": getCountdownHeaderFontWeight(brandKit, page, config),
		"--poll-countdown-date-text-color": getCountdownDateTextColor(brandKit, page, config),
		"--poll-countdown-date-text-size": "var(--text-" + getCountdownDateTextSize(brandKit, page, config) + ")",
		"--poll-countdown-date-text-alignment": getCountdownDateTextAlignment(brandKit, page, config),
		"--poll-countdown-date-font-weight": getCountdownDateFontWeight(brandKit, page, config),
		"--poll-helper-text-color": getHelperTextColor(brandKit, page, config),
		"--poll-helper-text-size": "var(--text-" + getHelperTextSize(brandKit, page, config) + ")",
		"--poll-helper-text-alignment": getHelperTextAlignment(brandKit, page, config),
		"--poll-helper-font-weight": getHelperFontWeight(brandKit, page, config),
		"--poll-results-helper-text-color": getResultsHelperTextColor(brandKit, page, config),
		"--poll-results-helper-text-size": "var(--text-" + getResultsHelperTextSize(brandKit, page, config) + ")",
		"--poll-results-helper-text-alignment": getResultsHelperTextAlignment(brandKit, page, config),
		"--poll-results-helper-font-weight": getResultsHelperFontWeight(brandKit, page, config),
		"--poll-skip-button-size": getSkipButtonSize(brandKit, config),
		"--poll-skip-button-border-radius": getSkipButtonBorderRadius(brandKit, config),
		"--poll-skip-button-background-color": getSkipButtonBackgroundColor(brandKit, page, config),
		"--poll-skip-button-background-hover-color": getHoverColor(getSkipButtonBackgroundColor(brandKit, page, config)),
		"--poll-skip-button-border-color": getSkipButtonBorderColor(brandKit, page, config),
		"--poll-skip-button-text-color": getSkipButtonTextColor(brandKit, page, config),
		"--poll-submit-button-size": getSubmitButtonSize(brandKit, config),
		"--poll-submit-button-border-radius": getSubmitButtonBorderRadius(brandKit, config),
		"--poll-submit-button-background-color": getSubmitButtonBackgroundColor(brandKit, page, config),
		"--poll-submit-button-background-hover-color": getHoverColor(getSubmitButtonBackgroundColor(brandKit, page, config)),
		"--poll-submit-button-border-color": getSubmitButtonBorderColor(brandKit, page, config),
		"--poll-submit-button-text-color": getSubmitButtonTextColor(brandKit, page, config),
		"--poll-complete-button-size": getCompleteButtonSize(brandKit, config),
		"--poll-complete-button-border-radius": getCompleteButtonBorderRadius(brandKit, config),
		"--poll-complete-button-background-color": getCompleteButtonBackgroundColor(brandKit, page, config),
		"--poll-complete-button-background-hover-color": getHoverColor(getCompleteButtonBackgroundColor(brandKit, page, config)),
		"--poll-complete-button-border-color": getCompleteButtonBorderColor(brandKit, page, config),
		"--poll-complete-button-text-color": getCompleteButtonTextColor(brandKit, page, config),
		"--poll-restart-button-size": getRestartButtonSize(brandKit, config),
		"--poll-restart-button-border-radius": getRestartButtonBorderRadius(brandKit, config),
		"--poll-restart-button-background-color": getRestartButtonBackgroundColor(brandKit, page, config),
		"--poll-restart-button-background-hover-color": getHoverColor(getRestartButtonBackgroundColor(brandKit, page, config)),
		"--poll-restart-button-border-color": getRestartButtonBorderColor(brandKit, page, config),
		"--poll-restart-button-text-color": getRestartButtonTextColor(brandKit, page, config),
	});
}
